import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import {
  BarChart3,
  AlertTriangle,
  CheckCircle,
  FileSearch,
  Users,
  ClipboardList,
  MessageCircleQuestion,
  Download,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Sparkles,
  Cpu,
  Trash2,
  Archive,
  FileText,
  Copy,
  X,
} from 'lucide-react'
import * as api from '../services/api'
import type {
  GapAnalysisResult,
  KickoffQuestionsResult,
  AnalysisType,
  QuestionType,
  AnalysisJobStatusResponse,
  Gap,
  KickoffQuestion,
} from '../types'

// Fun loading messages
const loadingMessages = [
  'Spinning up 20 GPUs just for you...',
  'Teaching AI to read project docs...',
  'Azure cluster is crunching your content...',
  'Consulting the digital oracle...',
  'Feeding documents to hungry neural networks...',
  'Performing advanced procrastination detection...',
  'Scanning for missing deadlines and forgotten tasks...',
  'Running sophisticated "find the gaps" algorithm...',
  'Deploying an army of virtual consultants...',
  'Calculating risk levels with extreme precision...',
  'Cross-referencing with best practices database...',
  'Making your documents feel inadequate...',
  'Turning coffee into insights...',
  'Engaging quantum buzzword generators...',
]

const analysisTypeConfig: Record<
  AnalysisType,
  { icon: typeof BarChart3; color: string; title: string; description: string }
> = {
  wbs_completeness: {
    icon: ClipboardList,
    color: 'blue',
    title: 'WBS Completeness',
    description: 'Missing tasks, deliverables, and milestones',
  },
  sow_coverage: {
    icon: FileSearch,
    color: 'purple',
    title: 'SOW Coverage',
    description: 'SOW requirements and WBS alignment',
  },
  risk: {
    icon: AlertTriangle,
    color: 'amber',
    title: 'Risk Analysis',
    description: 'Potential risks from documentation',
  },
  resource: {
    icon: Users,
    color: 'cyan',
    title: 'Resource Gaps',
    description: 'Resource allocation issues',
  },
}

const questionTypeConfig: Record<
  QuestionType,
  { icon: typeof MessageCircleQuestion; color: string; title: string; description: string }
> = {
  internal: {
    icon: Users,
    color: 'green',
    title: 'Internal Questions',
    description: 'Questions for internal team kickoff',
  },
  customer: {
    icon: MessageCircleQuestion,
    color: 'blue',
    title: 'Customer Questions',
    description: 'Questions to clarify with customer',
  },
}

function getSeverityBadge(severity: string) {
  switch (severity) {
    case 'critical':
      return <span className="badge-red">Critical</span>
    case 'high':
      return <span className="badge-yellow">High</span>
    case 'medium':
      return <span className="badge-blue">Medium</span>
    case 'low':
      return <span className="badge-gray">Low</span>
    default:
      return <span className="badge-gray">{severity}</span>
  }
}

function GapResultCard({ result, onDelete }: { result: GapAnalysisResult; onDelete?: () => void }) {
  const [expanded, setExpanded] = useState(true) // Default expanded for preview
  const config = analysisTypeConfig[result.analysis_type]
  const Icon = config?.icon || BarChart3

  // Parse gaps if it's a string (JSONB sometimes returns as string)
  const gaps: Gap[] = Array.isArray(result.gaps)
    ? result.gaps
    : typeof result.gaps === 'string'
      ? JSON.parse(result.gaps)
      : []

  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${config?.color || 'blue'}-500/20`}
          >
            <Icon className={`w-6 h-6 text-${config?.color || 'blue'}-400`} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-100">
              {config?.title || result.analysis_type}
            </h3>
            <p className="text-sm text-slate-400 mt-1">
              {gaps.length} gaps found • {new Date(result.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => api.downloadGapAnalysisCSV(result.project_id, result.analysis_type)}
            className="btn-ghost btn-sm"
            title="Download CSV"
          >
            <Download className="w-4 h-4" />
          </button>
          {onDelete && (
            <button
              onClick={() => {
                if (window.confirm(`Yeet this ${config?.title || 'analysis'} into the void?`)) {
                  onDelete()
                }
              }}
              className="btn-ghost btn-sm text-red-400 hover:text-red-300"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button onClick={() => setExpanded(!expanded)} className="btn-ghost btn-icon btn-sm">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="mt-4 p-4 bg-slate-800/30 rounded-lg">
        <p className="text-slate-300 text-sm">{result.summary}</p>
      </div>

      {/* Expanded Details */}
      {expanded && gaps.length > 0 && (
        <div className="mt-4 space-y-3">
          <h4 className="text-sm font-medium text-slate-400">Gap Details</h4>
          {gaps.map((gap, index) => (
            <div key={index} className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
              <div className="flex items-start justify-between mb-2">
                <h5 className="font-medium text-slate-200">{gap.category}</h5>
                {getSeverityBadge(gap.severity)}
              </div>
              <p className="text-sm text-slate-400 mb-2">{gap.description}</p>
              {gap.recommendation && (
                <div className="flex items-start gap-2 mt-2 p-2 bg-slate-900/50 rounded">
                  <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
                  <p className="text-sm text-slate-300">{gap.recommendation}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function KickoffResultCard({
  result,
  onDelete,
}: {
  result: KickoffQuestionsResult
  onDelete?: () => void
}) {
  const [expanded, setExpanded] = useState(true) // Default expanded for preview
  const config = questionTypeConfig[result.question_type]
  const Icon = config?.icon || MessageCircleQuestion

  // Parse questions if it's a string (JSONB sometimes returns as string)
  const questions: KickoffQuestion[] = Array.isArray(result.questions)
    ? result.questions
    : typeof result.questions === 'string'
      ? JSON.parse(result.questions)
      : []

  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${config?.color || 'green'}-500/20`}
          >
            <Icon className={`w-6 h-6 text-${config?.color || 'green'}-400`} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-100">
              {config?.title || result.question_type}
            </h3>
            <p className="text-sm text-slate-400 mt-1">
              {questions.length} questions • {new Date(result.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => api.downloadKickoffQuestionsCSV(result.project_id, result.question_type)}
            className="btn-ghost btn-sm"
            title="Download CSV"
          >
            <Download className="w-4 h-4" />
          </button>
          {onDelete && (
            <button
              onClick={() => {
                if (
                  window.confirm(
                    `Banish these ${config?.title || 'questions'} to the shadow realm?`
                  )
                ) {
                  onDelete()
                }
              }}
              className="btn-ghost btn-sm text-red-400 hover:text-red-300"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button onClick={() => setExpanded(!expanded)} className="btn-ghost btn-icon btn-sm">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {expanded && questions.length > 0 && (
        <div className="mt-4 space-y-2">
          {questions.map((q, index) => (
            <div key={index} className="flex items-start gap-3 p-3 bg-slate-800/30 rounded-lg">
              <span className="w-6 h-6 bg-slate-700/50 text-slate-400 rounded-full text-xs font-medium flex items-center justify-center flex-shrink-0">
                {index + 1}
              </span>
              <div className="flex-1">
                <p className="text-slate-200">{q.question}</p>
                {q.source_context && (
                  <p className="text-sm text-slate-500 mt-1">{q.source_context}</p>
                )}
                {q.category && <span className="badge-gray mt-2">{q.category}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

interface Sparkle {
  id: number
  x: number
  y: number
  size: number
  opacity: number
  color: string
}

interface LoadingAnimationProps {
  message: string
  jobStatus?: AnalysisJobStatusResponse | null
}

function LoadingAnimation({ message, jobStatus }: LoadingAnimationProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [sparkles, setSparkles] = useState<Sparkle[]>([])
  const sparkleIdRef = useRef(0)

  // Mouse trail effect
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const colors = ['#a78bfa', '#22d3ee', '#fbbf24', '#f472b6', '#34d399']

    const handleMouseMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      // Only create sparkles if mouse is inside container
      if (x < 0 || x > rect.width || y < 0 || y > rect.height) return

      // Create new sparkle
      const newSparkle: Sparkle = {
        id: sparkleIdRef.current++,
        x,
        y,
        size: Math.random() * 12 + 6,
        opacity: 1,
        color: colors[Math.floor(Math.random() * colors.length)],
      }

      setSparkles((prev) => [...prev.slice(-20), newSparkle]) // Keep max 20 sparkles
    }

    container.addEventListener('mousemove', handleMouseMove)
    return () => container.removeEventListener('mousemove', handleMouseMove)
  }, [])

  // Fade out sparkles
  useEffect(() => {
    if (sparkles.length === 0) return

    const interval = setInterval(() => {
      setSparkles((prev) =>
        prev
          .map((s) => ({ ...s, opacity: s.opacity - 0.05, size: s.size * 0.95 }))
          .filter((s) => s.opacity > 0)
      )
    }, 50)

    return () => clearInterval(interval)
  }, [sparkles.length])

  const progressPercent = jobStatus?.progress_percent ?? 0
  const currentStep = jobStatus?.current_step ?? 0
  const totalSteps = jobStatus?.total_steps ?? 6

  return (
    <div
      ref={containerRef}
      className="card bg-gradient-to-br from-violet-500/10 to-cyan-500/10 border-violet-500/30 relative overflow-hidden cursor-crosshair"
    >
      {/* Sparkle trail */}
      {sparkles.map((sparkle) => (
        <div
          key={sparkle.id}
          className="absolute pointer-events-none"
          style={{
            left: sparkle.x,
            top: sparkle.y,
            transform: 'translate(-50%, -50%)',
            opacity: sparkle.opacity,
            transition: 'opacity 0.1s, transform 0.1s',
          }}
        >
          <svg
            width={sparkle.size}
            height={sparkle.size}
            viewBox="0 0 24 24"
            fill={sparkle.color}
            className="animate-spin"
            style={{ animationDuration: '1s' }}
          >
            <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
          </svg>
        </div>
      ))}

      <div className="flex flex-col items-center justify-center py-12 relative z-10">
        {/* Animated CPU/Brain icon */}
        <div className="relative mb-6">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-500/30 to-cyan-500/30 flex items-center justify-center animate-pulse">
            <Cpu className="w-10 h-10 text-violet-400" />
          </div>
          {/* Orbiting dots */}
          <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s' }}>
            <div className="absolute -top-1 left-1/2 w-2 h-2 bg-cyan-400 rounded-full shadow-lg shadow-cyan-400/50" />
          </div>
          <div
            className="absolute inset-0 animate-spin"
            style={{ animationDuration: '2s', animationDirection: 'reverse' }}
          >
            <div className="absolute -bottom-1 left-1/2 w-2 h-2 bg-violet-400 rounded-full shadow-lg shadow-violet-400/50" />
          </div>
          <div className="absolute inset-0 animate-spin" style={{ animationDuration: '4s' }}>
            <div className="absolute top-1/2 -right-1 w-2 h-2 bg-amber-400 rounded-full shadow-lg shadow-amber-400/50" />
          </div>
        </div>

        {/* Real progress bar */}
        <div className="w-64 h-2 bg-slate-700/50 rounded-full overflow-hidden mb-2">
          <div
            className="h-full bg-gradient-to-r from-violet-500 to-cyan-500 rounded-full transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        {/* Progress text */}
        <p className="text-slate-400 text-sm mb-4">
          Step {currentStep} of {totalSteps} ({progressPercent}%)
        </p>

        {/* Step list */}
        {jobStatus?.steps && (
          <div className="w-full max-w-md mb-6 space-y-2">
            {jobStatus.steps.map((step, idx) => (
              <div
                key={idx}
                className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all ${
                  step.status === 'running'
                    ? 'bg-violet-500/20 border border-violet-500/30'
                    : step.status === 'completed'
                      ? 'bg-green-500/10'
                      : step.status === 'failed'
                        ? 'bg-red-500/10'
                        : 'bg-slate-800/30'
                }`}
              >
                {step.status === 'completed' ? (
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                ) : step.status === 'running' ? (
                  <div className="w-4 h-4 border-2 border-violet-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                ) : step.status === 'failed' ? (
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                ) : (
                  <div className="w-4 h-4 border-2 border-slate-600 rounded-full flex-shrink-0" />
                )}
                <span
                  className={`text-sm ${
                    step.status === 'running'
                      ? 'text-violet-300 font-medium'
                      : step.status === 'completed'
                        ? 'text-green-400'
                        : step.status === 'failed'
                          ? 'text-red-400'
                          : 'text-slate-500'
                  }`}
                >
                  {step.name}
                </span>
                {step.error && <span className="text-xs text-red-400 ml-auto">{step.error}</span>}
              </div>
            ))}
          </div>
        )}

        {/* Fun message */}
        <p className="text-slate-300 text-center font-medium">{message}</p>
        <p className="text-slate-500 text-sm mt-2">Move your mouse around for sparkles</p>
      </div>
    </div>
  )
}

export default function Analysis() {
  const { projectId } = useParams<{ projectId: string }>()
  const [gapResults, setGapResults] = useState<GapAnalysisResult[]>([])
  const [kickoffResults, setKickoffResults] = useState<KickoffQuestionsResult[]>([])
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState(loadingMessages[0])
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'gaps' | 'kickoff'>('gaps')
  const [jobStatus, setJobStatus] = useState<AnalysisJobStatusResponse | null>(null)
  const [showSummary, setShowSummary] = useState(false)
  const [summaryContent, setSummaryContent] = useState<string | null>(null)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Rotate loading messages
  useEffect(() => {
    if (!running) return
    const interval = setInterval(() => {
      setLoadingMessage(loadingMessages[Math.floor(Math.random() * loadingMessages.length)])
    }, 3000)
    return () => clearInterval(interval)
  }, [running])

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
      }
    }
  }, [])

  const fetchResults = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    try {
      const [gapsRes, kickoffRes] = await Promise.all([
        api.getGapAnalysisResults(projectId),
        api.getKickoffQuestions(projectId),
      ])
      setGapResults(gapsRes.data)
      setKickoffResults(kickoffRes.data)
    } catch (err) {
      console.error('Failed to fetch analysis results:', err)
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    fetchResults()
  }, [fetchResults])

  const pollJobStatus = useCallback(
    async (jobId: string) => {
      if (!projectId) return

      try {
        const response = await api.getAnalysisJobStatus(projectId, jobId)
        setJobStatus(response.data)

        if (response.data.status === 'completed' || response.data.status === 'failed') {
          // Stop polling
          if (pollingRef.current) {
            clearInterval(pollingRef.current)
            pollingRef.current = null
          }

          if (response.data.status === 'completed') {
            await fetchResults()
          } else if (response.data.error_message) {
            setError(response.data.error_message)
          }

          setRunning(false)
          setJobStatus(null)
        }
      } catch (err) {
        console.error('Failed to poll job status:', err)
        // Don't stop polling on transient errors
      }
    },
    [projectId, fetchResults]
  )

  const runFullAnalysis = async () => {
    if (!projectId) return
    setRunning(true)
    setError(null)
    setJobStatus(null)
    setLoadingMessage(loadingMessages[Math.floor(Math.random() * loadingMessages.length)])

    try {
      // Start the analysis job
      const response = await api.runAllGapAnalyses(projectId)
      const jobId = response.data.job_id

      // Start polling for status
      pollingRef.current = setInterval(() => {
        pollJobStatus(jobId)
      }, 2000) // Poll every 2 seconds

      // Also poll immediately
      pollJobStatus(jobId)
    } catch (err: unknown) {
      console.error('Failed to start analysis:', err)
      const errorMessage =
        err instanceof Error && err.message.includes('429')
          ? 'Azure OpenAI is rate-limited. Please wait a few minutes and try again.'
          : 'Analysis failed to start. Please try again in a moment.'
      setError(errorMessage)
      setRunning(false)
    }
  }

  const handleDeleteGapAnalysis = async (analysisId: string) => {
    if (!projectId) return
    try {
      await api.deleteGapAnalysis(projectId, analysisId)
      setGapResults((prev) => prev.filter((r) => r.id !== analysisId))
    } catch (err) {
      console.error('Failed to delete gap analysis:', err)
      setError('Failed to delete analysis result')
    }
  }

  const handleDeleteKickoffQuestions = async (questionId: string) => {
    if (!projectId) return
    try {
      await api.deleteKickoffQuestions(projectId, questionId)
      setKickoffResults((prev) => prev.filter((r) => r.id !== questionId))
    } catch (err) {
      console.error('Failed to delete kickoff questions:', err)
      setError('Failed to delete kickoff questions')
    }
  }

  const openExecutiveSummary = async () => {
    if (!projectId) return
    setSummaryLoading(true)
    setShowSummary(true)
    try {
      const response = await api.getExecutiveSummary(projectId)
      setSummaryContent(response.data.summary)
    } catch (err) {
      console.error('Failed to fetch executive summary:', err)
      setSummaryContent('Failed to load executive summary.')
    } finally {
      setSummaryLoading(false)
    }
  }

  const copyToClipboard = async () => {
    if (!summaryContent) return
    try {
      await navigator.clipboard.writeText(summaryContent)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const hasResults = gapResults.length > 0 || kickoffResults.length > 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">AI Analysis</h1>
          <p className="text-slate-400 mt-1">
            Run comprehensive AI analysis on your project documents
          </p>
        </div>
        {!running && (
          <div className="flex items-center gap-3">
            {hasResults && (
              <>
                <button onClick={openExecutiveSummary} className="btn-secondary">
                  <FileText className="w-4 h-4" />
                  Executive Summary
                </button>
                <button
                  onClick={() => projectId && api.downloadAllAnalysisZIP(projectId)}
                  className="btn-secondary"
                >
                  <Archive className="w-4 h-4" />
                  Download All (ZIP)
                </button>
              </>
            )}
            <button onClick={runFullAnalysis} className="btn-primary">
              <Sparkles className="w-4 h-4" />
              {hasResults ? 'Re-run Analysis' : 'Run Full Analysis'}
            </button>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="alert-error">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Loading Animation */}
      {running && <LoadingAnimation message={loadingMessage} jobStatus={jobStatus} />}

      {/* Results */}
      {!running && (
        <>
          {/* Tabs */}
          {hasResults && (
            <div className="tabs">
              <button
                onClick={() => setActiveTab('gaps')}
                className={activeTab === 'gaps' ? 'tab-active' : 'tab'}
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Gap Analysis ({gapResults.length})
              </button>
              <button
                onClick={() => setActiveTab('kickoff')}
                className={activeTab === 'kickoff' ? 'tab-active' : 'tab'}
              >
                <MessageCircleQuestion className="w-4 h-4 mr-2" />
                Kickoff Questions ({kickoffResults.length})
              </button>
            </div>
          )}

          {/* Gap Analysis Tab */}
          {activeTab === 'gaps' && (
            <div className="space-y-4">
              {loading ? (
                <div className="card">
                  <div className="space-y-4">
                    {[1, 2].map((i) => (
                      <div key={i} className="flex items-center gap-4">
                        <div className="skeleton w-12 h-12 rounded-xl" />
                        <div className="flex-1">
                          <div className="skeleton h-5 w-48 mb-2" />
                          <div className="skeleton h-4 w-32" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : gapResults.length > 0 ? (
                gapResults.map((result) => (
                  <GapResultCard
                    key={result.id}
                    result={result}
                    onDelete={() => handleDeleteGapAnalysis(result.id)}
                  />
                ))
              ) : (
                <div className="card text-center py-12">
                  <div className="w-20 h-20 bg-gradient-to-br from-violet-500/20 to-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <BarChart3 className="w-10 h-10 text-violet-400" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-300 mb-2">No analysis yet</h3>
                  <p className="text-slate-500 mb-6 max-w-md mx-auto">
                    Click "Run Full Analysis" to have AI analyze your project documents for gaps,
                    risks, and generate kickoff questions.
                  </p>
                  <button onClick={runFullAnalysis} className="btn-primary">
                    <Sparkles className="w-4 h-4" />
                    Run Full Analysis
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Kickoff Questions Tab */}
          {activeTab === 'kickoff' && (
            <div className="space-y-4">
              {loading ? (
                <div className="card">
                  <div className="space-y-4">
                    {[1, 2].map((i) => (
                      <div key={i} className="flex items-center gap-4">
                        <div className="skeleton w-12 h-12 rounded-xl" />
                        <div className="flex-1">
                          <div className="skeleton h-5 w-48 mb-2" />
                          <div className="skeleton h-4 w-32" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : kickoffResults.length > 0 ? (
                kickoffResults.map((result) => (
                  <KickoffResultCard
                    key={result.id}
                    result={result}
                    onDelete={() => handleDeleteKickoffQuestions(result.id)}
                  />
                ))
              ) : (
                <div className="card text-center py-12">
                  <div className="w-20 h-20 bg-gradient-to-br from-green-500/20 to-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <MessageCircleQuestion className="w-10 h-10 text-green-400" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-300 mb-2">
                    No questions generated yet
                  </h3>
                  <p className="text-slate-500 mb-6 max-w-md mx-auto">
                    Run the full analysis to generate kickoff questions for your project meetings.
                  </p>
                  <button onClick={runFullAnalysis} className="btn-primary">
                    <Sparkles className="w-4 h-4" />
                    Run Full Analysis
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Executive Summary Modal */}
      {showSummary && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl border border-slate-700">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <h2 className="text-xl font-semibold text-slate-100">Executive Summary</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={copyToClipboard}
                  className="btn-secondary btn-sm"
                  title="Copy to clipboard"
                >
                  <Copy className="w-4 h-4" />
                  {copied ? 'Copied!' : 'Copy'}
                </button>
                <button
                  onClick={() => projectId && api.downloadExecutiveSummary(projectId)}
                  className="btn-secondary btn-sm"
                  title="Download as Markdown"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
                <button onClick={() => setShowSummary(false)} className="btn-ghost btn-icon btn-sm">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-6">
              {summaryLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <pre className="whitespace-pre-wrap font-mono text-sm text-slate-300 leading-relaxed">
                  {summaryContent}
                </pre>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-700 text-center">
              <p className="text-sm text-slate-500">
                Copy this summary to share via email, Teams, or Slack
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
